/**
 * ValveClientApprovalPage — the client's page, reached from the emailed link
 * (`/valve-approval?token=…`), no login. Shows what GRPS found and
 * recommends, the quote, the PDF and any files, and takes Approve / Decline
 * with the person's name and a comment. The link works once.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { DocumentArrowDownIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import Alert from '../../shared/Alert';
import Button from '../../shared/Button';
import LoadingSpinner from '../../shared/LoadingSpinner';
import { FIELD_INPUT_CLASS, FIELD_LABEL_CLASS } from '../../shared/FormInput';
import { TEXT } from '../../shared/Typography';
import { usePublicPageScroll } from '../../../hooks/usePublicPageScroll';
import { formatAud } from './valveWorkflow';

interface Inspection {
  reportNo: string;
  clientName?: string;
  siteName?: string;
  plantName?: string;
  inspectionDate?: string;
  title?: string;
  scopeOfWork?: string;
  inspectionFindings?: string;
  components: Array<{ pane: string; tagNumber?: string; manufacturer?: string; model?: string; serialNumber?: string }>;
  contactName?: string;
  requiredWork: Array<{ item: string; comment?: string }>;
  requiredWorkNotes?: string;
  quoteAmount?: number;
  quoteNotes?: string;
  hasPdf: boolean;
  files: Array<{ fileId: string; name?: string; size?: number }>;
  linkExpiresAt?: string;
}

const PANE_NAMES: Record<string, string> = {
  valve: 'Valve',
  actuator: 'Operator',
  positioner: 'Positioner',
  regulator: 'Regulator',
  solenoid: 'Solenoid',
  tubingFitting: 'Tubing / Fitting',
};

const dateAu = (v?: string) => (v ? new Date(v).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');

export default function ValveClientApprovalPage() {
  usePublicPageScroll();
  const [status, setStatus] = useState<'loading' | 'ready' | 'sending' | 'done' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [data, setData] = useState<Inspection | null>(null);
  const [decision, setDecision] = useState<'approved' | 'declined' | ''>('');
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [result, setResult] = useState<'approved' | 'declined' | null>(null);

  const token = new URLSearchParams(window.location.search).get('token') || '';
  const base = `/api/valve-client-approval/${encodeURIComponent(token)}`;

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('This link is missing its token. Please open it from the email you received.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(base);
        if (cancelled) return;
        setData(res.data);
        setName(res.data?.contactName || '');
        setStatus('ready');
      } catch (e: any) {
        if (cancelled) return;
        setErrorMsg(e?.response?.data?.error || 'Unable to load the inspection. The link may have expired.');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, base]);

  async function submit() {
    if (!decision) {
      setErrorMsg('Please choose Approve or Decline.');
      return;
    }
    if (!name.trim()) {
      setErrorMsg('Please enter your name.');
      return;
    }
    if (decision === 'declined' && !comment.trim()) {
      setErrorMsg('Please tell us why you are declining, so we can adjust the recommendation.');
      return;
    }
    setStatus('sending');
    setErrorMsg('');
    try {
      await axios.post(base, { decision, name: name.trim(), comment: comment.trim() });
      setResult(decision);
      setStatus('done');
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.error || 'Could not record your answer. Please try again.');
      setStatus('ready');
    }
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner message="Loading inspection…" />
      </div>
    );
  }

  if (status === 'error' && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <div className="text-red-600 text-4xl mb-4">&#9888;</div>
          <h2 className={`mb-2 ${TEXT.sectionTitle}`}>Unable to open this inspection</h2>
          <p className={TEXT.bodyMuted}>{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (status === 'done') {
    const approved = result === 'approved';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className={`bg-white rounded-lg shadow-sm border p-8 max-w-md text-center ${approved ? 'border-green-200' : 'border-amber-200'}`}>
          <div className={`text-4xl mb-4 ${approved ? 'text-green-600' : 'text-amber-600'}`}>{approved ? '✔' : '✖'}</div>
          <h2 className={`mb-2 ${TEXT.sectionTitle}`}>{approved ? 'Work approved' : 'Response recorded'}</h2>
          <p className={TEXT.bodyMuted}>
            {approved
              ? `Thank you. GR Production Services will proceed with the recommended work on ${data?.reportNo || 'this valve'} and send the final report once the overhaul is complete.`
              : 'Thank you. GR Production Services has been told you declined the recommended work and will be in touch.'}
          </p>
          <p className={`mt-3 ${TEXT.caption}`}>You may close this page.</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-blue-900 text-white px-6 py-4">
            <h1 className="text-xl font-bold">Valve inspection — approval requested</h1>
            <p className="text-blue-200 text-sm mt-1">
              Report {data.reportNo}
              {data.clientName ? ` · ${data.clientName}` : ''}
              {data.siteName ? ` · ${data.siteName}` : ''}
            </p>
          </div>

          <div className="p-6 space-y-6">
            <p className={TEXT.body}>
              {data.contactName ? `Hello ${data.contactName}, ` : ''}GR Production Services has inspected the valve below. Please review what we found and the work we recommend, then approve or decline at the bottom of this page.
            </p>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className={TEXT.detailLabel}>Inspection date</span>
                <div className="text-gray-900">{dateAu(data.inspectionDate)}</div>
              </div>
              <div>
                <span className={TEXT.detailLabel}>Plant</span>
                <div className="text-gray-900">{data.plantName || '—'}</div>
              </div>
              {data.title && (
                <div className="col-span-2">
                  <span className={TEXT.detailLabel}>Title</span>
                  <div className="text-gray-900">{data.title}</div>
                </div>
              )}
              {data.scopeOfWork && (
                <div className="col-span-2">
                  <span className={TEXT.detailLabel}>Scope of work</span>
                  <div className="text-gray-900 whitespace-pre-wrap">{data.scopeOfWork}</div>
                </div>
              )}
            </div>

            {data.components.length > 0 && (
              <div>
                <h3 className={`mb-2 ${TEXT.cardTitle}`}>Equipment</h3>
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr className="text-left">
                      <th className="px-3 py-1.5">Item</th>
                      <th className="px-3 py-1.5">Manufacturer / model</th>
                      <th className="px-3 py-1.5">Serial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.components.map(c => (
                      <tr key={c.pane} className="border-t border-gray-100">
                        <td className="px-3 py-1.5">
                          {PANE_NAMES[c.pane] || c.pane}
                          {c.tagNumber ? <div className={TEXT.caption}>{c.tagNumber}</div> : null}
                        </td>
                        <td className="px-3 py-1.5">{[c.manufacturer, c.model].filter(Boolean).join(' ') || '—'}</td>
                        <td className="px-3 py-1.5">{c.serialNumber || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data.inspectionFindings && (
              <div>
                <h3 className={`mb-2 ${TEXT.cardTitle}`}>What we found</h3>
                <p className={`whitespace-pre-wrap ${TEXT.body}`}>{data.inspectionFindings}</p>
              </div>
            )}

            <div>
              <h3 className={`mb-2 ${TEXT.cardTitle}`}>Recommended work</h3>
              {data.requiredWork.length > 0 ? (
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-900">
                  {data.requiredWork.map((r, i) => (
                    <li key={i}>
                      <span className="font-medium">{r.item}</span>
                      {r.comment ? <span className="text-gray-600"> — {r.comment}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              {data.requiredWorkNotes && <p className={`mt-2 whitespace-pre-wrap ${TEXT.body}`}>{data.requiredWorkNotes}</p>}
              {data.quoteAmount !== undefined && data.quoteAmount !== null && (
                <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3">
                  <span className={TEXT.detailLabel}>Quoted amount (AUD, ex GST)</span>
                  <div className="text-lg font-semibold text-gray-900">{formatAud(data.quoteAmount)}</div>
                  {data.quoteNotes && <div className={TEXT.caption}>{data.quoteNotes}</div>}
                </div>
              )}
            </div>

            {(data.hasPdf || data.files.length > 0) && (
              <div>
                <h3 className={`mb-2 ${TEXT.cardTitle}`}>Documents</h3>
                <ul className="space-y-1 text-sm">
                  {data.hasPdf && (
                    <li>
                      <a href={`${base}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:underline">
                        <DocumentArrowDownIcon className="h-4 w-4" /> Inspection report (PDF)
                      </a>
                    </li>
                  )}
                  {data.files.map(f => (
                    <li key={f.fileId}>
                      <a href={`${base}/files/${f.fileId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:underline">
                        <PaperClipIcon className="h-4 w-4" /> {f.name || 'Attachment'}
                      </a>
                      {f.size ? <span className={`ml-1 ${TEXT.caption}`}>{Math.round(f.size / 1024)} KB</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="border-t border-gray-200 pt-5">
              <h3 className={`mb-3 ${TEXT.cardTitle}`}>Your response</h3>
              {errorMsg && <Alert variant="error" message={errorMsg} className="mb-3" onClose={() => setErrorMsg('')} />}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => setDecision('approved')}
                  className={`rounded-lg border-2 p-4 text-left transition ${decision === 'approved' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}
                >
                  <div className="font-semibold text-gray-900">Approve</div>
                  <div className={TEXT.caption}>Proceed with the recommended work{data.quoteAmount !== undefined && data.quoteAmount !== null ? ` at ${formatAud(data.quoteAmount)}` : ''}.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setDecision('declined')}
                  className={`rounded-lg border-2 p-4 text-left transition ${decision === 'declined' ? 'border-red-600 bg-red-50' : 'border-gray-200 hover:border-red-300'}`}
                >
                  <div className="font-semibold text-gray-900">Decline</div>
                  <div className={TEXT.caption}>Do not proceed. Tell us why below.</div>
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="valve-approval-name" className={FIELD_LABEL_CLASS}>
                    Your name
                  </label>
                  <input id="valve-approval-name" type="text" value={name} onChange={e => setName(e.target.value)} className={FIELD_INPUT_CLASS} />
                </div>
                <div>
                  <label htmlFor="valve-approval-comment" className={FIELD_LABEL_CLASS}>
                    Comment {decision === 'declined' ? '(required)' : '(optional)'}
                  </label>
                  <textarea id="valve-approval-comment" rows={3} value={comment} onChange={e => setComment(e.target.value)} className={FIELD_INPUT_CLASS} placeholder={decision === 'declined' ? 'Why the work should not proceed, or what you would like changed' : 'Purchase order number, conditions, anything we should know'} />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className={TEXT.caption}>{data.linkExpiresAt ? `This link expires ${dateAu(data.linkExpiresAt)}.` : ''}</span>
                  <Button variant={decision === 'declined' ? 'danger' : 'primary'} onClick={() => void submit()} disabled={status === 'sending' || !decision}>
                    {status === 'sending' ? 'Sending…' : decision === 'declined' ? 'Decline the work' : 'Approve the work'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p className={`mt-4 text-center ${TEXT.caption}`}>GR Production Services · Questions? Reply to the email this link came from.</p>
      </div>
    </div>
  );
}
